import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompetence } from '../contexts/CompetenceContext';
import {
    Box, Typography, Paper, Grid, Button, TextField, Alert, CircularProgress,
    List, ListItem, ListItemText, Divider, Dialog, DialogTitle,
    DialogContent, DialogActions, Tooltip, Card, CardContent, CardActions
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    PlayArrow as PlayArrowIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
    HelpOutline as HelpOutlineIcon,
    Description as DescriptionIcon
} from '@mui/icons-material';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import { IconButton } from '@mui/material';

// Import axios instance and API functions from api.js
import axios, {
    API_URL,
    uploadFile as apiUploadFile,
    processMonth,
    checkMissingCostCenters as apiCheckMissingCostCenters,
    getBalancete,
    addBalanceteItem,
    updateBalanceteItem,
    deleteBalanceteItem,
    deleteBalanceteCompetencia
} from '../api/api';

// ====================================================================
// CONTEÚDO AUXILIAR DO SISTEMA
// ====================================================================

// 3. Componente Auxiliar (Substitui ./RelatorioAnomalias)
const RelatorioAnomalias = ({ mes, anomalias, onAnomaliasResolved }) => {
    if (anomalias === null) {
        return <Alert severity="warning">Aguardando análise de integridade do Balancete...</Alert>;
    }

    if (anomalias.length === 0) {
        return (
            <Alert severity="success" icon={<CheckCircleIcon fontSize="inherit" />}>
                Nenhuma anomalia crítica detectada para o mês de {mes}.
            </Alert>
        );
    }

    return (
        <Box>
            <Typography variant="h6" color="error" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <ErrorIcon sx={{ mr: 1 }} /> Anomalias Críticas ({anomalias.length}) em {mes}:
            </Typography>
            <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', mb: 2 }}>
                <List dense>
                    {anomalias.map((anomalia, index) => (
                        <React.Fragment key={index}>
                            <ListItem>
                                <ListItemText
                                    primary={`${anomalia.centro} - ${anomalia.descricao}`}
                                    secondary={`Desvio: ${anomalia.desvio.toFixed(2)}`}
                                    primaryTypographyProps={{ color: 'error', fontWeight: 'medium' }}
                                />
                            </ListItem>
                            {index < anomalias.length - 1 && <Divider />}
                        </React.Fragment>
                    ))}
                </List>
            </Paper>
            <Button
                variant="contained"
                color="error"
                onClick={onAnomaliasResolved}
                fullWidth
            >
                Ignorar Alertas e Continuar
            </Button>
        </Box>
    );
};
// ====================================================================

// Informações de layout esperado para cada tipo de arquivo
const FILE_LAYOUT_INFO = {
    balancete: {
        title: 'Layout do Balancete',
        columns: ['Conta', 'Centro de Custo', 'Valor'],
        example: 'Conta;Centro de Custo;Valor\n4.1.01.001;UTI;15000.00',
        description: 'Arquivo CSV com dados do balancete mensal'
    }
};

// Componente de Tooltip com informações de layout
const LayoutInfoTooltip = ({ fileType }) => {
    const info = FILE_LAYOUT_INFO[fileType];
    if (!info) return null;

    return (
        <Tooltip
            title={
                <Box sx={{ p: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {info.title}
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ mb: 1 }}>
                        <strong>Colunas obrigatórias:</strong>
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2, mb: 1 }}>
                        {info.columns.map((col, i) => (
                            <li key={i}><Typography variant="caption">{col}</Typography></li>
                        ))}
                    </Box>
                    <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', bgcolor: 'rgba(0,0,0,0.2)', p: 0.5, borderRadius: 1 }}>
                        {info.example.split('\n').map((line, i) => (
                            <div key={i}>{line}</div>
                        ))}
                    </Typography>
                </Box>
            }
            arrow
            placement="top"
        >
            <HelpOutlineIcon
                fontSize="small"
                sx={{
                    ml: 1,
                    color: 'info.main',
                    cursor: 'help',
                    verticalAlign: 'middle'
                }}
            />
        </Tooltip>
    );
};

const ProcessamentoPage = () => {
    const navigate = useNavigate();
    const { globalCompetence, setGlobalCompetence, availableMonths } = useCompetence();

    // Arquivos de entrada mensais
    const [balanceteFile, setBalanceteFile] = useState(null);
    // Server-side filenames (returned after upload)
    const [balanceteServerName, setBalanceteServerName] = useState(null);

    // Configuração do Plano de Contas (Apenas leitura/verificação)
    const [isPlanoContasConfigured, setIsPlanoContasConfigured] = useState(false);
    const [planoContasName, setPlanoContasName] = useState('');

    // Usamos um formato de data ISO para garantir a ordenação histórica
    const [mes, setMes] = useState(globalCompetence || '');

    // Sync with global competence
    useEffect(() => {
        if (globalCompetence) {
            setMes(globalCompetence);
        }
    }, [globalCompetence]);

    // Estados de fluxo e UI
    const [status, setStatus] = useState('');
    const [isAnalysisComplete, setIsAnalysisComplete] = useState(false);
    const [anomalias, setAnomalias] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    // Missing cost centers modal
    const [missingCenters, setMissingCenters] = useState([]);
    const [showMissingCentersModal, setShowMissingCentersModal] = useState(false);

    // Data Status
    const [balanceteData, setBalanceteData] = useState([]);
    const [loadingData, setLoadingData] = useState(false);

    // CRUD State
    const [openModal, setOpenModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({ conta: '', centro_custo: '', valor: '' });
    const [deleteConfirmation, setDeleteConfirmation] = useState(null);
    const [ccOptions, setCcOptions] = useState([]);
    const [contaOptions, setContaOptions] = useState([]);

    // CRUD Handlers
    const handleOpenAdd = () => {
        setEditingItem(null);
        setFormData({ conta: '', centro_custo: '', valor: '' });
        setOpenModal(true);
    };

    const handleOpenEdit = (item) => {
        setEditingItem(item);
        setFormData({ conta: item.conta, centro_custo: item.centro_custo, valor: item.valor });
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setEditingItem(null);
    };

    const handleSave = async () => {
        try {
            const payload = {
                ...formData,
                valor: parseFloat(formData.valor)
            };

            if (editingItem) {
                await updateBalanceteItem(editingItem.id, payload);
                setStatus("Item atualizado com sucesso!");
            } else {
                await addBalanceteItem({ ...payload, mes });
                setStatus("Item adicionado com sucesso!");
            }
            handleCloseModal();
            // Refresh data
            const res = await getBalancete(mes);
            setBalanceteData(res.data);
        } catch (error) {
            console.error("Erro ao salvar item:", error);
            setStatus("Erro ao salvar item. Verifique os dados.");
        }
    };

    const handleDelete = async () => {
        if (!deleteConfirmation) return;
        try {
            await deleteBalanceteItem(deleteConfirmation);
            setStatus("Item excluído com sucesso!");
            setDeleteConfirmation(null);
            // Refresh data
            const res = await getBalancete(mes);
            setBalanceteData(res.data);
        } catch (error) {
            console.error("Erro ao excluir item:", error);
            setStatus("Erro ao excluir item.");
        }
    };

    const handleDeleteAllBalancete = async () => {
        if (!mes) {
            setStatus("Selecione uma competência primeiro.");
            return;
        }

        if (!window.confirm(`Tem certeza que deseja excluir TODOS os ${balanceteData.length} registros do Balancete da competência ${mes}? Esta ação não pode ser desfeita.`)) {
            return;
        }

        try {
            await deleteBalanceteCompetencia(mes);
            setStatus(`Todos os registros do Balancete foram excluídos com sucesso!`);
            // Refresh data
            const res = await getBalancete(mes);
            setBalanceteData(res.data);
        } catch (error) {
            console.error("Erro ao excluir balancete:", error);
            setStatus("Erro ao excluir registros do balancete.");
        }
    };

    // Função auxiliar para formatar a data (Ex: 2025-05 -> Maio 2025)
    const formatMonthForDisplay = (isoDate) => {
        if (!isoDate) return 'Mês de Referência';
        const [year, month] = isoDate.split('-');
        // Cria a data usando UTC para evitar problemas de fuso horário que podem atrasar o dia/mês
        const date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, 1));
        return date.toLocaleString('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' });
    };

    // Função para verificar se o Plano de Contas está configurado
    useEffect(() => {
        const checkConfig = async () => {
            try {
                // Checa o nome do arquivo do Plano de Contas salvo no DB
                const response = await axios.get(`${API_URL}/config/plano_contas_filename`);
                if (response.data && response.data !== '') {
                    setIsPlanoContasConfigured(true);
                    setPlanoContasName(response.data);
                } else {
                    setIsPlanoContasConfigured(false);
                    setPlanoContasName('');
                    setStatus("Atenção: O Plano de Contas não está configurado. Configure antes de processar.");
                }
            } catch (error) {
                console.error("Erro ao verificar Plano de Contas configurado.", error);
                setIsPlanoContasConfigured(false);
                setStatus("Erro de comunicação ao verificar a configuração do Plano de Contas.");
            }
        };
        const loadCCs = async () => {
            try {
                const res = await axios.get(`${API_URL}/config/centros_custo`);
                if (res.data) setCcOptions(res.data.map(cc => cc.nome).sort());
            } catch (err) {
                console.error("Erro ao carregar CCs:", err);
            }
        };
        const loadContas = async () => {
            try {
                const res = await axios.get(`${API_URL}/config/plano_contas_data`);
                if (res.data) setContaOptions(res.data);
            } catch (err) {
                console.error("Erro ao carregar Contas:", err);
            }
        };
        checkConfig();
        loadCCs();
        loadContas();
    }, []);

    // Fetch Data Status when month changes
    useEffect(() => {
        const fetchDataStatus = async () => {
            if (!mes) {
                setBalanceteData([]);
                return;
            }
            setLoadingData(true);
            try {
                // Fetch Balancete
                const balRes = await getBalancete(mes);
                setBalanceteData(balRes.data || []);
            } catch (error) {
                console.error("Erro ao buscar status dos dados:", error);
            } finally {
                setLoadingData(false);
            }
        };
        fetchDataStatus();
    }, [mes]);

    const runAnomalyAnalysis = async (filename) => {
        setAnomalias(null);
        setIsAnalysisComplete(false);
        setStatus("Iniciando análise de integridade...");

        try {
            const response = await axios.post(`${API_URL}/analisar_anomalias`, {
                filename: filename,
                mes: mes
            });

            setAnomalias(response.data.anomalias);
            setIsAnalysisComplete(true);

            if (response.data.anomalias.length === 0) {
                setStatus("Pronto para processar. Nenhuma anomalia crítica encontrada.");
            } else {
                setStatus(`Alerta! O Guardião encontrou ${response.data.anomalias.length} anomalias. Revise antes de processar.`);
            }

        } catch (err) {
            setAnomalias([]);
            setIsAnalysisComplete(true);
            setStatus("A análise de anomalias falhou. Processamento pode ser arriscado.");
            console.error("Erro na API de Anomalias:", err);
        }
    };

    const handleFileUpload = async (file, type) => {
        if (!file) return null;

        setStatus(`Enviando arquivo de ${type}...`);
        setIsUploading(true);
        try {
            // Pass 'mes' to upload function so backend can save to staging table
            const response = await apiUploadFile(type, file, mes);

            const total = response.data.soma_total;
            const totalMsg = total !== undefined ? ` Total: ${total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}` : '';

            setStatus(`Arquivo de ${type} enviado com sucesso!${totalMsg}`);

            // Return full data structure
            return {
                filename: response.data.filename || file.name,
                warnings: response.data.warnings,
                soma_total: response.data.soma_total,
                full_response: response.data
            };
        } catch (error) {
            const errorMessage = error.response?.data?.mensagem || `Erro no upload do arquivo de ${type}.`;
            setStatus(errorMessage);
            console.error(error);
            return null;
        } finally {
            setIsUploading(false);
        }
    };

    // Lógica para lidar com o upload do balancete (que aciona a análise)
    const handleBalanceteUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setBalanceteFile(file);
        const uploadResult = await handleFileUpload(file, 'balancete');

        if (uploadResult && uploadResult.filename) {
            const serverName = uploadResult.filename;
            setBalanceteServerName(serverName);

            // Show warnings if provided (and not just empty list)
            if (uploadResult.warnings && uploadResult.warnings.length > 0) {
                // If the only warning is about missing cost centers, we might ignore it here 
                // because the specific modal below will handle it better.
                // But for now, let's append to status so user sees something is up.
                const warningMsg = uploadResult.warnings.join('; ');
                setStatus(prev => `${prev} | Avisos: ${warningMsg}`);
            }

            await runAnomalyAnalysis(serverName);

            // Check for missing cost centers
            try {
                // api.js checkMissingCostCenters returns axios response, so we need .data
                const response = await apiCheckMissingCostCenters(serverName);
                if (response.data.status === 'Sucesso' && response.data.nao_cadastrados && response.data.nao_cadastrados.length > 0) {
                    setMissingCenters(response.data.nao_cadastrados);
                    setShowMissingCentersModal(true);
                }
            } catch (error) {
                console.error('Erro ao verificar centros de custo:', error);
            }

            // Refresh balancete data
            if (mes) {
                try {
                    const res = await getBalancete(mes);
                    setBalanceteData(res.data);
                } catch (error) {
                    console.error("Erro ao atualizar balancete:", error);
                }
            }
        } else {
            setAnomalias(null);
            setIsAnalysisComplete(false);
        }
        setIsAnalysisComplete(false);
    };

    const handleProcessClick = async () => {
        if (!balanceteData.length && !balanceteFile) {
            setStatus("Erro: O Balancete é obrigatório.");
            return;
        }

        if (!mes) {
            setStatus("Erro: Selecione uma competência.");
            return;
        }

        if (!isPlanoContasConfigured) {
            setStatus("Erro: O Plano de Contas deve ser carregado na seção de configurações antes do processamento. Por favor, configure-o primeiro.");
            return;
        }

        if (!isAnalysisComplete && balanceteFile) {
            setStatus("Por favor, aguarde a conclusão da análise do Guardião de Dados.");
            await runAnomalyAnalysis(balanceteServerName || balanceteFile.name);
            if (!isAnalysisComplete) return;
        }

        // Verifica se há anomalias e exige confirmação. Substituindo window.confirm.
        if (anomalias && anomalias.length > 0) {
            console.warn("ALERTA: Anomalias detectadas. Processando mesmo assim, conforme lógica do sistema.");
        }

        await runMainProcessing();
    };

    const runMainProcessing = async () => {
        setIsProcessing(true);
        setStatus(`Processando mês ${formatMonthForDisplay(mes)} com o Motor de Rateio...`);

        try {
            // Use server name for balancete
            // Production data comes from production_data table (uploaded via /producao page)
            await processMonth({
                mes,
                balancete_filename: balanceteServerName || (balanceteFile ? balanceteFile.name : null)
            });
            setStatus("Processamento concluído com sucesso! Verifique o Dashboard.");
        } catch (error) {
            const errorMessage = error.response?.data?.mensagem || "Ocorreu um erro inesperado no Motor de Rateio. Verifique o console do servidor.";
            setStatus(`Erro no Processamento: ${errorMessage}`);
        } finally {
            setIsProcessing(false);
        }
    };

    const isReadyToProcess = (balanceteData.length > 0 || balanceteFile) && isPlanoContasConfigured;

    // Tooltip message for disabled button
    const getButtonTooltip = () => {
        if (!isPlanoContasConfigured) return 'Configure o Plano de Contas primeiro';
        if (!balanceteData.length && !balanceteFile) return 'Faça upload do Balancete';
        if (isUploading) return 'Aguarde o upload finalizar';
        if (isProcessing) return 'Processamento em andamento';
        return '';
    };


    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', borderBottom: 1, borderColor: 'divider', pb: 2 }}>
                Processamento de Competência
            </Typography>

            {/* 1. SELEÇÃO DE COMPETÊNCIA */}
            <Paper sx={{ p: 3, mb: 4 }}>
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <TextField
                            select
                            label="Mês de Referência"
                            value={mes}
                            onChange={e => {
                                setMes(e.target.value);
                                setGlobalCompetence(e.target.value);
                            }}
                            fullWidth
                            disabled={isProcessing || isUploading}
                            SelectProps={{
                                native: true,
                            }}
                        >
                            <option value="" disabled>Selecione uma competência</option>
                            {availableMonths.map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {mes && (
                            <Typography variant="subtitle1" color="text.secondary">
                                Status: <strong>{loadingData ? 'Verificando...' : 'Pronto para verificação'}</strong>
                            </Typography>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            {mes && (
                <>

                    {/* 2. DASHBOARD DE STATUS - APENAS BALANCETE */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        {/* Balancete Card */}
                        <Grid item xs={12} md={6}>
                            <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Typography variant="h6" component="div" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                        <DescriptionIcon color="primary" sx={{ mr: 1 }} /> Balancete
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Dados contábeis do mês.
                                    </Typography>
                                    <Divider sx={{ my: 1.5 }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        {balanceteData.length > 0 ? (
                                            <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                                        ) : (
                                            <ErrorIcon color="error" sx={{ mr: 1 }} />
                                        )}
                                        <Typography variant="body1" fontWeight="bold">
                                            {balanceteData.length > 0 ? 'Importado' : 'Pendente'}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2">
                                        Registros: {balanceteData.length}
                                    </Typography>
                                </CardContent>
                                <CardActions>
                                    <Button
                                        component="label"
                                        variant="outlined"
                                        fullWidth
                                        startIcon={<CloudUploadIcon />}
                                        disabled={isProcessing || isUploading}
                                    >
                                        {balanceteData.length > 0 ? "Reenviar Balancete" : "Upload Balancete"}
                                        <input
                                            type="file"
                                            hidden
                                            accept=".csv"
                                            onChange={handleBalanceteUpload}
                                        />
                                    </Button>
                                    <LayoutInfoTooltip fileType="balancete" />
                                </CardActions>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Tabela de Dados do Balancete */}
                    {balanceteData.length > 0 && (
                        <Paper sx={{ p: 3, mb: 4 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>Dados do Balancete ({balanceteData.length} registros)</span>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        color="error"
                                        onClick={handleDeleteAllBalancete}
                                    >
                                        Excluir Todos
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<FaPlus />}
                                        onClick={handleOpenAdd}
                                    >
                                        Adicionar Registro
                                    </Button>
                                </Box>
                            </Typography>
                            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Conta</th>
                                            <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Centro de Custo</th>
                                            <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #ddd' }}>Valor</th>
                                            <th style={{ textAlign: 'center', padding: '8px', borderBottom: '1px solid #ddd' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {balanceteData.map((row, index) => (
                                            <tr key={index}>
                                                <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{row.conta}</td>
                                                <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{row.centro_custo}</td>
                                                <td style={{ padding: '8px', borderBottom: '1px solid #eee', textAlign: 'right' }}>
                                                    {parseFloat(row.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                                </td>
                                                <td style={{ padding: '8px', borderBottom: '1px solid #eee', textAlign: 'center' }}>
                                                    <IconButton size="small" color="primary" onClick={() => handleOpenEdit(row)}>
                                                        <FaEdit />
                                                    </IconButton>
                                                    <IconButton size="small" color="error" onClick={() => setDeleteConfirmation(row.id)}>
                                                        <FaTrash />
                                                    </IconButton>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </Box>
                        </Paper>
                    )}

                    {/* 3. GUARDIÃO DE DADOS & EXECUÇÃO */}

                    {/* Relatório de Anomalias (Aparece após upload do Balancete) */}
                    {(balanceteFile && anomalias !== null) && (
                        <Paper sx={{ p: 3, mb: 4, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>Verificação de Integridade (Guardião)</Typography>
                            <RelatorioAnomalias
                                mes={formatMonthForDisplay(mes)}
                                anomalias={anomalias}
                                onAnomaliasResolved={() => {
                                    setAnomalias([]);
                                    setIsAnalysisComplete(true);
                                    setStatus("Alerta de anomalias ignorado. Pronto para processar.");
                                }}
                            />
                        </Paper>
                    )}

                    {/* Botão de Processamento */}
                    <Paper sx={{ p: 4, mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', bgcolor: '#f8f9fa' }}>
                        <Typography variant="h6" gutterBottom color="text.secondary">
                            Pronto para processar?
                        </Typography>
                        <Tooltip title={getButtonTooltip()} arrow placement="top">
                            <span>
                                <Button
                                    variant="contained"
                                    color={isReadyToProcess && !isProcessing && isPlanoContasConfigured ? "success" : "inherit"}
                                    size="large"
                                    onClick={handleProcessClick}
                                    disabled={!isReadyToProcess || isProcessing || isUploading || !isPlanoContasConfigured}
                                    startIcon={isProcessing ? <CircularProgress size={24} color="inherit" /> : <PlayArrowIcon />}
                                    sx={{ px: 4, py: 1.5, fontSize: '1.1rem', fontWeight: 'bold', minWidth: 300 }}
                                >
                                    {isProcessing ? "Processando..." : `Processar ${formatMonthForDisplay(mes)}`}
                                </Button>
                            </span>
                        </Tooltip>
                        {!isPlanoContasConfigured && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                O Plano de Contas não está configurado.
                            </Alert>
                        )}
                    </Paper>
                </>
            )}

            {/* STATUS E MENSAGENS */}
            {status && (
                <Alert severity="info" icon={<InfoIcon fontSize="inherit" />} sx={{ mt: 2 }}>
                    <Typography variant="body1">
                        <strong>Status: </strong> {status}
                    </Typography>
                </Alert>
            )}

            {/* Missing Cost Centers Modal */}
            <Dialog open={showMissingCentersModal} onClose={() => setShowMissingCentersModal(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Typography variant="h6" color="warning.main" sx={{ display: 'flex', alignItems: 'center' }}>
                        <WarningIcon sx={{ mr: 1 }} />
                        Centros de Custo Não Cadastrados
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Foram encontrados {missingCenters.length} centros de custo no balancete que ainda não estão cadastrados no sistema.
                    </Alert>
                    <Typography variant="body2" gutterBottom>
                        <strong>Centros faltantes:</strong>
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto', bgcolor: 'grey.50' }}>
                        <List dense>
                            {missingCenters.map((center, index) => (
                                <ListItem key={index}>
                                    <ListItemText
                                        primary={center}
                                        primaryTypographyProps={{ fontFamily: 'monospace', color: 'text.primary' }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                    <Alert severity="info" sx={{ mt: 2 }}>
                        Recomendamos cadastrar esses centros de custo antes de prosseguir com o processamento para evitar erros.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowMissingCentersModal(false)} color="inherit">
                        Ignorar e Continuar
                    </Button>
                    <Button
                        onClick={() => {
                            setShowMissingCentersModal(false);
                            navigate('/centros-de-custo', {
                                state: { missingCenters: missingCenters }
                            });
                        }}
                        variant="contained"
                        color="primary"
                    >
                        Ir para Centros de Custo
                    </Button>
                </DialogActions>
            </Dialog>


            {/* Add/Edit Modal */}
            <Dialog open={openModal} onClose={handleCloseModal}>
                <DialogTitle>{editingItem ? 'Editar Item' : 'Adicionar Item'}</DialogTitle>
                <DialogContent>
                    <TextField
                        select
                        margin="dense"
                        label="Conta Contábil"
                        fullWidth
                        value={formData.conta}
                        onChange={(e) => setFormData({ ...formData, conta: e.target.value })}
                        SelectProps={{ native: true }}
                    >
                        <option value="" disabled>Selecione...</option>
                        {contaOptions.map((acc, index) => (
                            <option key={index} value={acc.Conta}>
                                {acc.Conta} - {acc.Descricao}
                            </option>
                        ))}
                    </TextField>
                    <TextField
                        select
                        margin="dense"
                        label="Centro de Custo"
                        fullWidth
                        value={formData.centro_custo}
                        onChange={(e) => setFormData({ ...formData, centro_custo: e.target.value })}
                        SelectProps={{ native: true }}
                    >
                        <option value="" disabled>Selecione...</option>
                        {ccOptions.map(cc => <option key={cc} value={cc}>{cc}</option>)}
                    </TextField>
                    <TextField
                        margin="dense"
                        label="Valor"
                        type="number"
                        fullWidth
                        value={formData.valor}
                        onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained">Salvar</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog open={!!deleteConfirmation} onClose={() => setDeleteConfirmation(null)}>
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <Typography>Tem certeza que deseja excluir este item?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmation(null)}>Cancelar</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">Excluir</Button>
                </DialogActions>
            </Dialog>
        </Box >
    );
};

export default ProcessamentoPage;
